import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Store, 
  Leaf, 
  PiggyBank, 
  Tag,
  Check,
  Loader2,
  TrendingDown,
  ShoppingCart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShoppingPreferences = () => {
  const [preferences, setPreferences] = useState({
    private_label_preference: 'prefer',
    organic_preference: 'neutral',
    sale_first: true,
    budget_mode: 'balanced'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/store-preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPreferences(data.preferences);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async (newPrefs) => {
    setSaving(true);
    setSaved(false);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/store-preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newPrefs)
      });
      if (response.ok) {
        setPreferences(newPrefs);
        setSaved(true);
        // Calculate soft preview
        calculatePreview(newPrefs);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
    } finally {
      setSaving(false);
    }
  };

  const calculatePreview = (prefs) => {
    // Soft preview - not a full recalculation
    let estimatedSavings = 0;
    let changes = [];

    if (prefs.private_label_preference === 'prefer') {
      estimatedSavings += 12;
      changes.push('Store brands selected');
    }
    if (prefs.sale_first) {
      estimatedSavings += 8;
      changes.push('Sale items prioritized');
    }
    if (prefs.organic_preference === 'prefer') {
      estimatedSavings -= 15;
      changes.push('Organic produce selected');
    }
    if (prefs.budget_mode === 'strict') {
      estimatedSavings += 6;
    }

    setPreview({
      estimatedSavings: estimatedSavings > 0 ? estimatedSavings : 0,
      changes: changes.slice(0, 3)
    });
  };

  const handleToggle = (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    savePreferences(newPrefs);
  };

  if (loading) {
    return (
      <Card className="border-sage-200">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-sage-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-sage-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-sage-600" />
            <CardTitle className="text-lg font-['Playfair_Display']">Shopping Preferences</CardTitle>
          </div>
          {saved && (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <Check className="h-3 w-3 mr-1" /> Saved
            </Badge>
          )}
        </div>
        <CardDescription className="text-sm text-muted-foreground">
          Applied when building your grocery list
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Prefer Store Brands */}
        <div className="flex items-center justify-between py-2 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Store className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-sm">Prefer store brands</p>
              <p className="text-xs text-muted-foreground">Lower-cost alternatives when available</p>
            </div>
          </div>
          <Switch
            checked={preferences.private_label_preference === 'prefer'}
            onCheckedChange={(checked) => 
              handleToggle('private_label_preference', checked ? 'prefer' : 'neutral')
            }
            disabled={saving}
          />
        </div>

        {/* Organic When Available */}
        <div className="flex items-center justify-between py-2 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <Leaf className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-sm">Organic when available</p>
              <p className="text-xs text-muted-foreground">Select organic produce and proteins</p>
            </div>
          </div>
          <Switch
            checked={preferences.organic_preference === 'prefer'}
            onCheckedChange={(checked) => 
              handleToggle('organic_preference', checked ? 'prefer' : 'neutral')
            }
            disabled={saving}
          />
        </div>

        {/* Sale Items First */}
        <div className="flex items-center justify-between py-2 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-pink-50 rounded-lg">
              <Tag className="h-4 w-4 text-pink-600" />
            </div>
            <div>
              <p className="font-medium text-sm">Sale items first</p>
              <p className="text-xs text-muted-foreground">Prioritize items currently on sale</p>
            </div>
          </div>
          <Switch
            checked={preferences.sale_first === true}
            onCheckedChange={(checked) => handleToggle('sale_first', checked)}
            disabled={saving}
          />
        </div>

        {/* Budget-Friendly Options */}
        <div className="flex items-center justify-between py-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 rounded-lg">
              <PiggyBank className="h-4 w-4 text-amber-600" />
            </div>
            <div>
              <p className="font-medium text-sm">Budget-friendly mode</p>
              <p className="text-xs text-muted-foreground">Maximize savings on every item</p>
            </div>
          </div>
          <Switch
            checked={preferences.budget_mode === 'strict'}
            onCheckedChange={(checked) => 
              handleToggle('budget_mode', checked ? 'strict' : 'balanced')
            }
            disabled={saving}
          />
        </div>

        {/* Soft Preview - only shows when preferences have impact */}
        {preview && preview.changes.length > 0 && (
          <div className="mt-4 p-3 bg-sage-50 rounded-lg border border-sage-100">
            <div className="flex items-center gap-2 text-sm text-sage-700">
              <TrendingDown className="h-4 w-4" />
              <span className="font-medium">
                {preview.estimatedSavings > 0 
                  ? `Est. savings: ~$${preview.estimatedSavings} per cart`
                  : 'Premium options selected'
                }
              </span>
            </div>
            <p className="text-xs text-sage-600 mt-1">
              {preview.changes.join(' • ')}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ShoppingPreferences;
